import React, { useState, useContext } from 'react';
import { SummarySection, FinanceSummary } from '@dmm/lib-react-ui-components';
import PropTypes from 'prop-types';
import { injectIntl } from 'react-intl';
import get from 'lodash/get';
import { getConfig, PortalConfigContext } from '../../../config/portal';
import { listingPropTypes } from '../../../utils/commonPropTypes';
import { TITLE_FORMAT } from '../../../constants/boatDetails/index';
import {
  getTitle,
  getLocation,
  getPrice,
  currencyFormattingOptions,
  listingAttribute,
  showPriceCalculation
} from '../../../utils/listingHelper';
import { getFormattedPriceDrop } from '../../../utils/commonHelper';
import { scrollToComponent } from '../../../utils/scrollTo';
import {syncTranslationElements} from '../../../tppServices/translations/intlManager';
import { makeFinanceableSummaryTrackingEvent } from '../../../utils/trident';

const BDPBoatSummary = (props) => {
  const {
    listing,
    customUom,
    intl: { locale },
    supportsTridentCalculator,
    monthlyPrice,
    lite
  } = props;
  const { messages, t } = syncTranslationElements(props.intl);

  const { attributeText } = listingAttribute(listing);

  const customOptions = currencyFormattingOptions(
    get(props, 'intl.locale', null)
  );
  const price = getPrice(listing, customOptions);
  const taxStatus = get(listing, 'legal.taxStatus', undefined);
  const taxMessage = taxStatus ? t(messages.taxStatus[taxStatus]) : '';
  const context = useContext(PortalConfigContext);

  const renderPriceDrop = (listing, locale) => {
    const portalConfig = getConfig();
    const previousPriceObj = get(listing, 'previousPrice', null);
    const previousPriceDate = new Date(
      get(previousPriceObj, 'revisedDate', null)
    );
    const today = new Date();
    const daysDifference =
      (today.getTime() - previousPriceDate.getTime()) / (1000 * 3600 * 24);

    if (
      !get(portalConfig, 'supports.priceDrop', false) ||
      daysDifference > 30
    ) {
      return null;
    }

    const formattingOptions = currencyFormattingOptions(locale);
    const priceDrop = getFormattedPriceDrop(
      previousPriceObj,
      get(listing, 'price', null),
      locale,
      formattingOptions
    );

    return priceDrop ? priceDrop : null;
  };

  const engineHours = listing?.propulsion?.engines?.length ? listing.propulsion.engines[0].hours : 0;

  const [paymentCalculatorCounter, setPaymentCalculatorCounter] = useState(0);

  const handlePaymentCalculatorLinkClick = (event) => {
    event.preventDefault();
    const customizeTracking = makeFinanceableSummaryTrackingEvent('customize', 'click');
    window.dataLayer.push(customizeTracking);
    window.requestAnimationFrame(() => {
      scrollToComponent('#scroll-to-calculator');
    });
    props.onClickPaymentLink(paymentCalculatorCounter);
    setPaymentCalculatorCounter(paymentCalculatorCounter + 1);
  };

  const titleFormat = `${TITLE_FORMAT} | {length}`;
  const title = getTitle(listing, false, titleFormat, locale, customUom);

  const showMonthlyPrice = showPriceCalculation(listing, context);

  const showFinanceSummary = showMonthlyPrice && supportsTridentCalculator && monthlyPrice;
  const engineHoursVal = engineHours
    ? `${engineHours} ${t(messages.boatDetails.summary.engineHoursText)}`
    : '';

  return (
    <>
      <div id="bdp-boat-summary" className="bdp-boat-summary">
        <SummarySection
          status={attributeText}
          title={title}
          location={getLocation(listing)}
          price={`${price} ${!/\d/.test(price) ? '' : taxMessage}`}
          priceDrop={renderPriceDrop(listing)}
          priceDropAlt={t(messages.boatDetails.priceDrop)}
          engineHours={engineHoursVal}
          classNames={{ tppPriceSection: true }}

        />
        {showFinanceSummary && (
          <div className="finance-summary">
            <FinanceSummary
              paymentText={`${t(
                messages.boatDetails.summary.paymentText
              )}$${monthlyPrice}/${t(messages.boatDetails.monthlyPrice)}`}
              linkText={t(messages.boatDetails.summary.linkText)}
              content={t(messages.boatDetails.summary.tooltipText)}
              useHover={false}
              background="#fff"
              direction="up-start"
              className="finance-summary-tooltip"
              classNames={{
                lite: lite
              }}
              onClickCustomize={handlePaymentCalculatorLinkClick}
              hyperlinkAttributes={{
                'data-reporting-click-internal-link-type': 'click',
                'data-reporting-click-internal-link-id': 'customize'
              }}
            />
          </div>
        )}
      </div>
    </>
  );
};

BDPBoatSummary.propTypes = {
  listing: listingPropTypes,
  customUom: PropTypes.shape({
    name: PropTypes.string,
    abbr: PropTypes.string.isRequired,
    symbol: PropTypes.string
  }),
  intl: PropTypes.shape({
    locale: PropTypes.string.isRequired
  }).isRequired,
  supportsTridentCalculator: PropTypes.bool,
  lite: PropTypes.bool,
  monthlyPrice: PropTypes.string,
  onClickPaymentLink: PropTypes.func
};

export default injectIntl(BDPBoatSummary);
